// framework
import React from "react";
import PropTypes from "prop-types";
// styles
import styles from "./CashSlot.module.scss";
// constants
import { getCashImage } from "constants/cash";
import { GAME_STATES } from "constants/constants";

/**
 * Determines how many stacks each player should have based on total player count
 * @param {number} playerCount - Total number of players
 * @returns {number} Number of stacks per player
 */
function getStacksPerPlayer(playerCount) {
    if (playerCount === 1) return 4;
    if (playerCount >= 2 && playerCount <= 4) return 2;
    return 1; // 5 or more players
}

/**
 * Builds a cash matrix for all slots and players
 * Matrix structure: Array of slots, each containing Array of stack objects
 * Stack object: { color: string, count: number }
 * 
 * @param {Array} players - Array of player objects
 * @param {number} slotCount - Number of answer slots
 * @returns {Array<Array<{color: string, count: number}>>} Cash matrix
 */
export function buildCashMatrix(players, slotCount) {
    const playerCount = (players ?? []).length;
    const stacksPerPlayer = getStacksPerPlayer(playerCount);

    // Initialize matrix: one array per slot
    const matrix = Array.from({ length: slotCount }, () => []);

    (players ?? []).forEach((player) => {
        const colorKey = player?.mdmdData?.cashColor;

        for (let slotIdx = 0; slotIdx < slotCount; slotIdx++) {
            const placedMoney = Number(player?.mdmdData?.placedMoney?.[slotIdx] ?? 0);
            const totalNotes = placedMoney > 0 ? Math.floor(placedMoney / 25000) : 0;

            if (totalNotes === 0) {
                // Still add empty stacks to maintain consistent structure
                for (let stackIdx = 0; stackIdx < stacksPerPlayer; stackIdx++) {
                    matrix[slotIdx].push({
                        color: colorKey,
                        count: 0
                    });
                }
                continue;
            }

            // Distribute notes across multiple stacks for this player
            const notesPerStack = Math.ceil(totalNotes / stacksPerPlayer);

            for (let stackIdx = 0; stackIdx < stacksPerPlayer; stackIdx++) {
                const startNote = stackIdx * notesPerStack;
                const endNote = Math.min((stackIdx + 1) * notesPerStack, totalNotes);
                const notesInThisStack = Math.max(0, endNote - startNote);

                matrix[slotIdx].push({
                    color: colorKey,
                    count: notesInThisStack
                });
            }
        }
    });

    return matrix;
}

export default function CashSlot({ className, slotIdx, slotCount, cashMatrix, stackLevel, gameState, yOffset = "0%", cashGap ="0%", maxCashWidth = "25%"}) {
    const slotIndex1 = slotIdx + 1;
    const stacksForThisSlot = cashMatrix[slotIdx] ?? [];

    return (
        <div className={`${styles.root} ${className || ""}`}>
            <div className={styles.stackList} style={cashGap !== undefined ? { gap: cashGap } : undefined} >
                {stacksForThisSlot.map((stackData, stackIdx) => {
                    const targetCount = stackData.count;
                    const visibleCount = Math.min(stackLevel, targetCount);
                    const colorKey = stackData.color;
                    const img = getCashImage(colorKey, slotCount, slotIndex1, gameState === GAME_STATES.REVEAL);

                    return (
                        <div key={`stack-${slotIdx}-${stackIdx}`} style={maxCashWidth !== undefined ? { width: maxCashWidth } : undefined}  className={styles.stack} data-color={colorKey} >
                            {Array.from({ length: visibleCount }).map((__, noteIdx) => (
                                <img key={`note-${noteIdx}`} className={styles.cashImg} src={img} alt="" aria-hidden="true"
                                    style={yOffset !== undefined ? { marginTop: yOffset } : undefined}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

CashSlot.propTypes = {
    className: PropTypes.string,
    slotIdx: PropTypes.number.isRequired,
    slotCount: PropTypes.number.isRequired,
    cashMatrix: PropTypes.arrayOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                color: PropTypes.string,
                count: PropTypes.number.isRequired,
            })
        )
    ).isRequired,
    stackLevel: PropTypes.number.isRequired,
    gameState: PropTypes.string,
    yOffset: PropTypes.string, // Add this
};